import { Injectable, isDevMode } from '@angular/core';
import { environment } from '../environments/environment';
import packageJson from '../../package.json';
import { FirebaseApp, initializeApp } from '@firebase/app';
import { Firestore, initializeFirestore, doc, getDoc, collection, Timestamp, addDoc, DocumentSnapshot, DocumentData } from '@firebase/firestore';
import { Auth, getAuth } from '@firebase/auth';
import { persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { EventLogActionType, EventLogSource, Role } from './common/enums';
import { BehaviorSubject, Observable, takeWhile } from 'rxjs';
import { Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NotificationDialogComponent, NotificationDialogModel } from './dialogs/notification-dialog/notification-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private _firebaseApp: FirebaseApp;
  private _firebaseAuth: Auth;
  private _firestore: Firestore;
  private _userRecord: BehaviorSubject<DocumentSnapshot<DocumentData> | null> = new BehaviorSubject<DocumentSnapshot<DocumentData> | null>(null);
  public readonly userRecordSubject$: Observable<DocumentSnapshot<DocumentData> | null> = this._userRecord.asObservable();
  public selectedSite: DocumentSnapshot<DocumentData> | undefined;
  public fullScreen = false;
  public siteInputCtrl = new UntypedFormControl('');
  public siteIdSource: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  siteIdChanged$ = this.siteIdSource.asObservable();

  // Cloud "parent account" (multi-site) view - lets eligible users switch the site picker to a parent account picker
  public selectedParentAccount: DocumentSnapshot<DocumentData> | undefined;
  public parentAccountInputCtrl = new UntypedFormControl('');
  public parentAccountIdSource: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  parentAccountIdChanged$ = this.parentAccountIdSource.asObservable();
  public cloudViewModeSource: BehaviorSubject<'site' | 'parentAccount'> = new BehaviorSubject<'site' | 'parentAccount'>('site');
  cloudViewModeChanged$ = this.cloudViewModeSource.asObservable();

  public EMAIL_CLIENT_RELATIONS = "clientrelations@proeconomy.com";
  public EMAIL_SERVICE_REPORT = "servicereports@proeconomy.com";
  public EMAIL_VEHICLE_CHECKLIST = "vehiclechecklist@proeconomy.com";
  public EMAIL_SCHEDULER = "scheduling@proeconomy.com";  

  public isLoggedIn = false;
  public user: string | null = '';
  public userRecord: DocumentSnapshot<DocumentData, DocumentData> | null = null;
  public userDisplayName: string = "";
  public userSiteNumbers: string[] = [];
  public userRoles: number[] = [];
  public userId: string | undefined;

  public version = '';
  public buildTime = '';
  public isProduction = true;

  constructor(private router: Router, private dialog: MatDialog) {
    this._firebaseApp = initializeApp(environment.firebase);
    this._firebaseAuth = getAuth(this._firebaseApp);
    this._firestore = initializeFirestore(this._firebaseApp, { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) });  // Set offline mode
    this._firebaseAuth.onAuthStateChanged((user: any) => {
      if (user) {
        this.fetchUserRecord();
      } else {
        this.router.navigate(['']);
      }
    });    
  }

  public init() {
    this.version = packageJson.version;
    this.buildTime = environment.timeStamp;
    this.isProduction = environment.production;
  }

  initEmailsForDev () {
    if (isDevMode() && !!this.user) {
      this.EMAIL_CLIENT_RELATIONS = this.user;
      this.EMAIL_SERVICE_REPORT = this.user;
      this.EMAIL_VEHICLE_CHECKLIST = this.user;
      this.EMAIL_SCHEDULER = this.user;
    }
  }

  get firebaseAuth() {
    return this._firebaseAuth;
  }

  get firebaseApp(): FirebaseApp {
    return this._firebaseApp;
  }

  get firestore() {
    return this._firestore;
  }

  get isParentAccountView(): boolean {
    return this.cloudViewModeSource.value === 'parentAccount';
  }

  // The dev site is a separate Firebase Hosting site (dev-proeconomy-tetras) sharing the production Firebase project
  get isDevEnvironment(): boolean {
    return location.hostname.startsWith('dev-proeconomy-tetras');
  }

  // Returns false when the signed-in user is denied access to this environment (and has been signed out)
  async fetchUserRecord(): Promise<boolean> {
    if (this.firebaseAuth.currentUser) {
      const docRef = doc(this.firestore, 'users', this.firebaseAuth.currentUser.uid);
      const docSnap = await getDoc(docRef);
      // A concurrent call may have already signed the user out
      if (!this.firebaseAuth.currentUser) {
        return false;
      }
      const roles: number[] = docSnap.exists() ? (docSnap.get('roles') ?? []) : [];
      if (this.isDevEnvironment && !roles.includes(Role.Admin) && !roles.includes(Role.DevEnvironment)) {
        await this.firebaseAuth.signOut();
        this.dialog.open(NotificationDialogComponent, {
          data: new NotificationDialogModel('Access Denied', 'You do not have permission to access the Dev environment.')
        });
        return false;
      }
      this.isLoggedIn = true;
      this.user = this.firebaseAuth.currentUser.email;
      if (docSnap.exists()) {
        this.userRecord = docSnap;
        this._userRecord.next(docSnap);
        this.userDisplayName = this.userRecord?.get('name');
        this.userSiteNumbers = this.userRecord?.get('siteNumbers') ?? [];
        this.userRoles = this.userRecord?.get('roles') ?? [];
        this.userId = this.userRecord?.id;
      }
      this.initEmailsForDev();
    }
    return true;
  }

  public userHasRole(role: Role): boolean {
    return this.userRecord?.get('roles')?.includes(role);
  }

  public canUseParentAccountView(): boolean {
    const parentAccountIds: string[] = this.userRecord?.get('parentAccountIds') ?? [];
    return this.userHasRole(Role.CloudParentAccountView) && parentAccountIds.length > 0;
  }
  
  public canActivate(roles: Role[]): Promise<boolean> {
    return new Promise((resolve) => {
      this.userRecordSubject$.pipe(
        takeWhile(userRecord => userRecord === null),
      ).subscribe({
        complete: () => {
          let userHasRole = false;
          for (const role of roles) {
            if(this.userHasRole(role)) {
              userHasRole = true;
              break;
            }
          }
          if (!userHasRole) {
            this.router.navigate(['']);
          }
          resolve(userHasRole);
        }
      })
    });
  }

  public isLoggedInCheck(): Promise<boolean> {
    return new Promise((resolve) => {
      this.userRecordSubject$.pipe(
        takeWhile(userRecord => userRecord === null),
      ).subscribe({
        complete: () => {
          resolve(this.isLoggedIn);
        }
      })
    });
  } 

  logout() {
    this.firebaseAuth.signOut().then(() => {
      this.isLoggedIn = false;
      this.userDisplayName = "";
      this.userSiteNumbers = [];
      this.userRoles = [];
      this.userId = "";
      this.userRecord = null;
      this._userRecord.next(null);
    })
    .catch((err) => {
      console.log('Failed to logout: ', err);
    });    
  }

  logEvent(eventLogSource: EventLogSource, eventLogActionType: EventLogActionType, modifiedItemId: string, value?: any, siteId?: string) {
    try{
      const cleanValue = this.cleanValue(value);
      const coll = collection(this.firestore, "eventLogs");
      const record: Record<string, any> = {};
      record['eventLogSource'] = eventLogSource;
      record['eventLogActionType'] = eventLogActionType;
      record['eventTime'] = Timestamp.now()
      record['userId'] = this.userRecord?.id;
      record['userEmail'] = this.user;
      record['modifiedItemId'] = modifiedItemId;
      record['siteId'] = siteId ?? this.siteInputCtrl.value?.id ?? null;
      record['value'] = cleanValue ? cleanValue : null;
      addDoc(coll, record);
    } catch (error) {
      if (error instanceof Error) {
        console.error('Error message:', error.message);        
      } else {
        console.error('Unknown error:', error);
      }
    }
  }

  // Build a log record without writing it, so callers can include it in a writeBatch.
  buildLogRecord(eventLogSource: EventLogSource, eventLogActionType: EventLogActionType, modifiedItemId: string, value?: any): Record<string, any> {
    const cleanValue = this.cleanValue(value);
    const record: Record<string, any> = {};
    record['eventLogSource'] = eventLogSource;
    record['eventLogActionType'] = eventLogActionType;
    record['eventTime'] = Timestamp.now();
    record['userId'] = this.userRecord?.id;
    record['userEmail'] = this.user;
    record['modifiedItemId'] = modifiedItemId;
    record['siteId'] = this.siteInputCtrl.value?.id || null;
    record['value'] = cleanValue ? cleanValue : null;
    return record;
  }

  // Value objects destined for the event log can contain the special function deleteField() as values
  // The special deleteField() value removes a field with the given key from the document in the database
  // An object value of deleteField() causes addDoc() to blow up in logEvent() above, as it cannot be converted to a printable string
  // This function iterate through the object replacing deleteField() instances with the printable string "deleteField()"
  protected cleanValue(value?: any) : any {
    if (value && typeof value === "object") {
      const objValues: Record<string, any> = value;
      const newValues: Record<string, any> = {};
      for (const key of Object.keys(objValues)) {
        let objValue = objValues[key];
        if (objValue && objValue._methodName === "deleteField") {
          objValue = "deleteField()";
        }
        newValues[key] = objValue;
      }
      return newValues;
    } else {
      return value;
    }
  }
}
